/**
 * Single entry point for the Lucide icons used in this app.
 *
 * The lucide-react barrel evaluates every icon on import. Vitest does not tree-shake,
 * so each test file that reached a component paid for the whole barrel. Importing each
 * icon from its own module avoids that.
 *
 * Per-icon modules ship no .d.ts, so each value is re-typed as LucideIcon here to keep full
 * type safety. Export names match the barrel exactly, including deprecated aliases such
 * as Loader2, Trash2 and XCircle.
 *
 * Add new icons to this file instead of importing from lucide-react directly.
 */

import type { LucideIcon } from 'lucide-react';

export type { LucideIcon };

import icon_Activity from 'lucide-react/dist/esm/icons/activity.mjs';
import icon_AlertCircle from 'lucide-react/dist/esm/icons/circle-alert.mjs';
import icon_AlertOctagon from 'lucide-react/dist/esm/icons/octagon-alert.mjs';
import icon_AlertTriangle from 'lucide-react/dist/esm/icons/triangle-alert.mjs';
import icon_Archive from 'lucide-react/dist/esm/icons/archive.mjs';
import icon_ArrowDown from 'lucide-react/dist/esm/icons/arrow-down.mjs';
import icon_ArrowDownLeft from 'lucide-react/dist/esm/icons/arrow-down-left.mjs';
import icon_ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.mjs';
import icon_ArrowLeftRight from 'lucide-react/dist/esm/icons/arrow-left-right.mjs';
import icon_ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.mjs';
import icon_ArrowRightLeft from 'lucide-react/dist/esm/icons/arrow-right-left.mjs';
import icon_ArrowUp from 'lucide-react/dist/esm/icons/arrow-up.mjs';
import icon_ArrowUpDown from 'lucide-react/dist/esm/icons/arrow-up-down.mjs';
import icon_ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right.mjs';
import icon_Ban from 'lucide-react/dist/esm/icons/ban.mjs';
import icon_Banknote from 'lucide-react/dist/esm/icons/banknote.mjs';
import icon_BarChart3 from 'lucide-react/dist/esm/icons/chart-column.mjs';
import icon_Bed from 'lucide-react/dist/esm/icons/bed.mjs';
import icon_Bell from 'lucide-react/dist/esm/icons/bell.mjs';
import icon_Box from 'lucide-react/dist/esm/icons/box.mjs';
import icon_Briefcase from 'lucide-react/dist/esm/icons/briefcase.mjs';
import icon_Bug from 'lucide-react/dist/esm/icons/bug.mjs';
import icon_Building from 'lucide-react/dist/esm/icons/building.mjs';
import icon_Building2 from 'lucide-react/dist/esm/icons/building-complex.mjs';
import icon_Calculator from 'lucide-react/dist/esm/icons/calculator.mjs';
import icon_Calendar from 'lucide-react/dist/esm/icons/calendar.mjs';
import icon_CalendarCheck from 'lucide-react/dist/esm/icons/calendar-check.mjs';
import icon_CalendarDays from 'lucide-react/dist/esm/icons/calendar-days.mjs';
import icon_CalendarPlus from 'lucide-react/dist/esm/icons/calendar-plus.mjs';
import icon_CalendarSync from 'lucide-react/dist/esm/icons/calendar-sync.mjs';
import icon_Camera from 'lucide-react/dist/esm/icons/camera.mjs';
import icon_Car from 'lucide-react/dist/esm/icons/car.mjs';
import icon_ChartColumn from 'lucide-react/dist/esm/icons/chart-column.mjs';
import icon_Check from 'lucide-react/dist/esm/icons/check.mjs';
import icon_CheckCheck from 'lucide-react/dist/esm/icons/check-check.mjs';
import icon_CheckCircle2 from 'lucide-react/dist/esm/icons/circle-check.mjs';
import icon_ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs';
import icon_ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left.mjs';
import icon_ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.mjs';
import icon_ChevronsUpDown from 'lucide-react/dist/esm/icons/chevrons-up-down.mjs';
import icon_ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list.mjs';
import icon_Clock from 'lucide-react/dist/esm/icons/clock.mjs';
import icon_Clock3 from 'lucide-react/dist/esm/icons/clock-3.mjs';
import icon_Code from 'lucide-react/dist/esm/icons/code.mjs';
import icon_Coffee from 'lucide-react/dist/esm/icons/coffee.mjs';
import icon_Coins from 'lucide-react/dist/esm/icons/coins.mjs';
import icon_Cookie from 'lucide-react/dist/esm/icons/cookie.mjs';
import icon_Copy from 'lucide-react/dist/esm/icons/copy.mjs';
import icon_CreditCard from 'lucide-react/dist/esm/icons/credit-card.mjs';
import icon_Download from 'lucide-react/dist/esm/icons/download.mjs';
import icon_Droplets from 'lucide-react/dist/esm/icons/droplets.mjs';
import icon_Dumbbell from 'lucide-react/dist/esm/icons/dumbbell.mjs';
import icon_Edit2 from 'lucide-react/dist/esm/icons/pen.mjs';
import icon_Edit3 from 'lucide-react/dist/esm/icons/pen-line.mjs';
import icon_Eraser from 'lucide-react/dist/esm/icons/eraser.mjs';
import icon_Euro from 'lucide-react/dist/esm/icons/euro.mjs';
import icon_ExternalLink from 'lucide-react/dist/esm/icons/external-link.mjs';
import icon_Eye from 'lucide-react/dist/esm/icons/eye.mjs';
import icon_EyeOff from 'lucide-react/dist/esm/icons/eye-off.mjs';
import icon_FileArchive from 'lucide-react/dist/esm/icons/file-archive.mjs';
import icon_FileDown from 'lucide-react/dist/esm/icons/file-down.mjs';
import icon_FileSignature from 'lucide-react/dist/esm/icons/file-pen-line.mjs';
import icon_FileSpreadsheet from 'lucide-react/dist/esm/icons/file-spreadsheet.mjs';
import icon_FileText from 'lucide-react/dist/esm/icons/file-text.mjs';
import icon_FileWarning from 'lucide-react/dist/esm/icons/file-warning.mjs';
import icon_Fingerprint from 'lucide-react/dist/esm/icons/fingerprint-pattern.mjs';
import icon_Flame from 'lucide-react/dist/esm/icons/flame.mjs';
import icon_FlaskConical from 'lucide-react/dist/esm/icons/flask-conical.mjs';
import icon_FolderClosed from 'lucide-react/dist/esm/icons/folder-closed.mjs';
import icon_FolderOpen from 'lucide-react/dist/esm/icons/folder-open.mjs';
import icon_Gauge from 'lucide-react/dist/esm/icons/gauge.mjs';
import icon_Globe from 'lucide-react/dist/esm/icons/globe.mjs';
import icon_HardDrive from 'lucide-react/dist/esm/icons/hard-drive.mjs';
import icon_HardHat from 'lucide-react/dist/esm/icons/hard-hat.mjs';
import icon_Hash from 'lucide-react/dist/esm/icons/hash.mjs';
import icon_HelpCircle from 'lucide-react/dist/esm/icons/circle-question-mark.mjs';
import icon_History from 'lucide-react/dist/esm/icons/rotate-ccw-clock.mjs';
import icon_Home from 'lucide-react/dist/esm/icons/house.mjs';
import icon_Hourglass from 'lucide-react/dist/esm/icons/hourglass.mjs';
import icon_Image from 'lucide-react/dist/esm/icons/image.mjs';
import icon_Inbox from 'lucide-react/dist/esm/icons/inbox.mjs';
import icon_Info from 'lucide-react/dist/esm/icons/info.mjs';
import icon_Kanban from 'lucide-react/dist/esm/icons/kanban.mjs';
import icon_Key from 'lucide-react/dist/esm/icons/key.mjs';
import icon_KeyRound from 'lucide-react/dist/esm/icons/key-round.mjs';
import icon_Landmark from 'lucide-react/dist/esm/icons/landmark.mjs';
import icon_Languages from 'lucide-react/dist/esm/icons/languages.mjs';
import icon_Layers from 'lucide-react/dist/esm/icons/layers.mjs';
import icon_LayoutDashboard from 'lucide-react/dist/esm/icons/layout-dashboard.mjs';
import icon_LayoutGrid from 'lucide-react/dist/esm/icons/layout-grid.mjs';
import icon_Link2 from 'lucide-react/dist/esm/icons/link-2.mjs';
import icon_List from 'lucide-react/dist/esm/icons/list.mjs';
import icon_ListChecks from 'lucide-react/dist/esm/icons/list-checks.mjs';
import icon_ListFilter from 'lucide-react/dist/esm/icons/list-filter.mjs';
import icon_Loader2 from 'lucide-react/dist/esm/icons/loader-circle.mjs';
import icon_Lock from 'lucide-react/dist/esm/icons/lock.mjs';
import icon_LogOut from 'lucide-react/dist/esm/icons/log-out.mjs';
import icon_Mail from 'lucide-react/dist/esm/icons/mail.mjs';
import icon_MailCheck from 'lucide-react/dist/esm/icons/mail-check.mjs';
import icon_MapPin from 'lucide-react/dist/esm/icons/map-pin.mjs';
import icon_MapPinned from 'lucide-react/dist/esm/icons/map-pinned.mjs';
import icon_Maximize2 from 'lucide-react/dist/esm/icons/maximize-2.mjs';
import icon_Megaphone from 'lucide-react/dist/esm/icons/megaphone.mjs';
import icon_Menu from 'lucide-react/dist/esm/icons/menu.mjs';
import icon_Minus from 'lucide-react/dist/esm/icons/minus.mjs';
import icon_Moon from 'lucide-react/dist/esm/icons/moon.mjs';
import icon_Paintbrush from 'lucide-react/dist/esm/icons/paintbrush.mjs';
import icon_Paperclip from 'lucide-react/dist/esm/icons/paperclip.mjs';
import icon_PauseCircle from 'lucide-react/dist/esm/icons/circle-pause.mjs';
import icon_Pencil from 'lucide-react/dist/esm/icons/pencil.mjs';
import icon_PenSquare from 'lucide-react/dist/esm/icons/square-pen.mjs';
import icon_Percent from 'lucide-react/dist/esm/icons/percent.mjs';
import icon_Phone from 'lucide-react/dist/esm/icons/phone.mjs';
import icon_PieChart from 'lucide-react/dist/esm/icons/chart-pie.mjs';
import icon_PiggyBank from 'lucide-react/dist/esm/icons/piggy-bank.mjs';
import icon_Pin from 'lucide-react/dist/esm/icons/pin.mjs';
import icon_PlayCircle from 'lucide-react/dist/esm/icons/circle-play.mjs';
import icon_Plus from 'lucide-react/dist/esm/icons/plus.mjs';
import icon_Power from 'lucide-react/dist/esm/icons/power.mjs';
import icon_Printer from 'lucide-react/dist/esm/icons/printer.mjs';
import icon_QrCode from 'lucide-react/dist/esm/icons/qr-code.mjs';
import icon_Receipt from 'lucide-react/dist/esm/icons/receipt.mjs';
import icon_RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw.mjs';
import icon_Repeat from 'lucide-react/dist/esm/icons/repeat.mjs';
import icon_RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw.mjs';
import icon_RotateCw from 'lucide-react/dist/esm/icons/rotate-cw.mjs';
import icon_Save from 'lucide-react/dist/esm/icons/save.mjs';
import icon_Scale from 'lucide-react/dist/esm/icons/scale.mjs';
import icon_ScrollText from 'lucide-react/dist/esm/icons/scroll-text.mjs';
import icon_Search from 'lucide-react/dist/esm/icons/search.mjs';
import icon_SearchX from 'lucide-react/dist/esm/icons/search-x.mjs';
import icon_Send from 'lucide-react/dist/esm/icons/send.mjs';
import icon_Settings from 'lucide-react/dist/esm/icons/settings.mjs';
import icon_Settings2 from 'lucide-react/dist/esm/icons/settings-2.mjs';
import icon_Share2 from 'lucide-react/dist/esm/icons/share-2.mjs';
import icon_Shield from 'lucide-react/dist/esm/icons/shield.mjs';
import icon_ShieldAlert from 'lucide-react/dist/esm/icons/shield-alert.mjs';
import icon_ShieldCheck from 'lucide-react/dist/esm/icons/shield-check.mjs';
import icon_Siren from 'lucide-react/dist/esm/icons/siren.mjs';
import icon_Sliders from 'lucide-react/dist/esm/icons/sliders-vertical.mjs';
import icon_SlidersHorizontal from 'lucide-react/dist/esm/icons/sliders-horizontal.mjs';
import icon_Smartphone from 'lucide-react/dist/esm/icons/smartphone.mjs';
import icon_Sparkles from 'lucide-react/dist/esm/icons/sparkles.mjs';
import icon_Sprout from 'lucide-react/dist/esm/icons/sprout.mjs';
import icon_SquareParking from 'lucide-react/dist/esm/icons/square-parking.mjs';
import icon_Store from 'lucide-react/dist/esm/icons/store.mjs';
import icon_Sun from 'lucide-react/dist/esm/icons/sun.mjs';
import icon_Tag from 'lucide-react/dist/esm/icons/tag.mjs';
import icon_Target from 'lucide-react/dist/esm/icons/target.mjs';
import icon_Trash2 from 'lucide-react/dist/esm/icons/trash.mjs';
import icon_TreePine from 'lucide-react/dist/esm/icons/tree-pine.mjs';
import icon_Trees from 'lucide-react/dist/esm/icons/trees.mjs';
import icon_TrendingDown from 'lucide-react/dist/esm/icons/trending-down.mjs';
import icon_TrendingUp from 'lucide-react/dist/esm/icons/trending-up.mjs';
import icon_Trophy from 'lucide-react/dist/esm/icons/trophy.mjs';
import icon_Undo2 from 'lucide-react/dist/esm/icons/undo-2.mjs';
import icon_Upload from 'lucide-react/dist/esm/icons/upload.mjs';
import icon_UploadCloud from 'lucide-react/dist/esm/icons/cloud-upload.mjs';
import icon_User from 'lucide-react/dist/esm/icons/user.mjs';
import icon_UserCheck from 'lucide-react/dist/esm/icons/user-check.mjs';
import icon_UserCircle from 'lucide-react/dist/esm/icons/circle-user.mjs';
import icon_UserCog from 'lucide-react/dist/esm/icons/user-cog.mjs';
import icon_UserPlus from 'lucide-react/dist/esm/icons/user-plus.mjs';
import icon_Users from 'lucide-react/dist/esm/icons/users.mjs';
import icon_UserX from 'lucide-react/dist/esm/icons/user-x.mjs';
import icon_Vote from 'lucide-react/dist/esm/icons/vote.mjs';
import icon_Wallet from 'lucide-react/dist/esm/icons/wallet.mjs';
import icon_Waves from 'lucide-react/dist/esm/icons/waves-horizontal.mjs';
import icon_Wifi from 'lucide-react/dist/esm/icons/wifi.mjs';
import icon_Wrench from 'lucide-react/dist/esm/icons/wrench.mjs';
import icon_X from 'lucide-react/dist/esm/icons/x.mjs';
import icon_XCircle from 'lucide-react/dist/esm/icons/circle-x.mjs';
import icon_Zap from 'lucide-react/dist/esm/icons/zap.mjs';
import icon_ZoomIn from 'lucide-react/dist/esm/icons/zoom-in.mjs';
import icon_ZoomOut from 'lucide-react/dist/esm/icons/zoom-out.mjs';

export const Activity: LucideIcon = icon_Activity;
export const AlertCircle: LucideIcon = icon_AlertCircle;
export const AlertOctagon: LucideIcon = icon_AlertOctagon;
export const AlertTriangle: LucideIcon = icon_AlertTriangle;
export const Archive: LucideIcon = icon_Archive;
export const ArrowDown: LucideIcon = icon_ArrowDown;
export const ArrowDownLeft: LucideIcon = icon_ArrowDownLeft;
export const ArrowLeft: LucideIcon = icon_ArrowLeft;
export const ArrowLeftRight: LucideIcon = icon_ArrowLeftRight;
export const ArrowRight: LucideIcon = icon_ArrowRight;
export const ArrowRightLeft: LucideIcon = icon_ArrowRightLeft;
export const ArrowUp: LucideIcon = icon_ArrowUp;
export const ArrowUpDown: LucideIcon = icon_ArrowUpDown;
export const ArrowUpRight: LucideIcon = icon_ArrowUpRight;
export const Ban: LucideIcon = icon_Ban;
export const Banknote: LucideIcon = icon_Banknote;
export const BarChart3: LucideIcon = icon_BarChart3;
export const Bed: LucideIcon = icon_Bed;
export const Bell: LucideIcon = icon_Bell;
export const Box: LucideIcon = icon_Box;
export const Briefcase: LucideIcon = icon_Briefcase;
export const Bug: LucideIcon = icon_Bug;
export const Building: LucideIcon = icon_Building;
export const Building2: LucideIcon = icon_Building2;
export const Calculator: LucideIcon = icon_Calculator;
export const Calendar: LucideIcon = icon_Calendar;
export const CalendarCheck: LucideIcon = icon_CalendarCheck;
export const CalendarDays: LucideIcon = icon_CalendarDays;
export const CalendarPlus: LucideIcon = icon_CalendarPlus;
export const CalendarSync: LucideIcon = icon_CalendarSync;
export const Camera: LucideIcon = icon_Camera;
export const Car: LucideIcon = icon_Car;
export const ChartColumn: LucideIcon = icon_ChartColumn;
export const Check: LucideIcon = icon_Check;
export const CheckCheck: LucideIcon = icon_CheckCheck;
export const CheckCircle2: LucideIcon = icon_CheckCircle2;
export const ChevronDown: LucideIcon = icon_ChevronDown;
export const ChevronLeft: LucideIcon = icon_ChevronLeft;
export const ChevronRight: LucideIcon = icon_ChevronRight;
export const ChevronsUpDown: LucideIcon = icon_ChevronsUpDown;
export const ClipboardList: LucideIcon = icon_ClipboardList;
export const Clock: LucideIcon = icon_Clock;
export const Clock3: LucideIcon = icon_Clock3;
export const Code: LucideIcon = icon_Code;
export const Coffee: LucideIcon = icon_Coffee;
export const Coins: LucideIcon = icon_Coins;
export const Cookie: LucideIcon = icon_Cookie;
export const Copy: LucideIcon = icon_Copy;
export const CreditCard: LucideIcon = icon_CreditCard;
export const Download: LucideIcon = icon_Download;
export const Droplets: LucideIcon = icon_Droplets;
export const Dumbbell: LucideIcon = icon_Dumbbell;
export const Edit2: LucideIcon = icon_Edit2;
export const Edit3: LucideIcon = icon_Edit3;
export const Eraser: LucideIcon = icon_Eraser;
export const Euro: LucideIcon = icon_Euro;
export const ExternalLink: LucideIcon = icon_ExternalLink;
export const Eye: LucideIcon = icon_Eye;
export const EyeOff: LucideIcon = icon_EyeOff;
export const FileArchive: LucideIcon = icon_FileArchive;
export const FileDown: LucideIcon = icon_FileDown;
export const FileSignature: LucideIcon = icon_FileSignature;
export const FileSpreadsheet: LucideIcon = icon_FileSpreadsheet;
export const FileText: LucideIcon = icon_FileText;
export const FileWarning: LucideIcon = icon_FileWarning;
export const Fingerprint: LucideIcon = icon_Fingerprint;
export const Flame: LucideIcon = icon_Flame;
export const FlaskConical: LucideIcon = icon_FlaskConical;
export const FolderClosed: LucideIcon = icon_FolderClosed;
export const FolderOpen: LucideIcon = icon_FolderOpen;
export const Gauge: LucideIcon = icon_Gauge;
export const Globe: LucideIcon = icon_Globe;
export const HardDrive: LucideIcon = icon_HardDrive;
export const HardHat: LucideIcon = icon_HardHat;
export const Hash: LucideIcon = icon_Hash;
export const HelpCircle: LucideIcon = icon_HelpCircle;
export const History: LucideIcon = icon_History;
export const Home: LucideIcon = icon_Home;
export const Hourglass: LucideIcon = icon_Hourglass;
export const Image: LucideIcon = icon_Image;
export const Inbox: LucideIcon = icon_Inbox;
export const Info: LucideIcon = icon_Info;
export const Kanban: LucideIcon = icon_Kanban;
export const Key: LucideIcon = icon_Key;
export const KeyRound: LucideIcon = icon_KeyRound;
export const Landmark: LucideIcon = icon_Landmark;
export const Languages: LucideIcon = icon_Languages;
export const Layers: LucideIcon = icon_Layers;
export const LayoutDashboard: LucideIcon = icon_LayoutDashboard;
export const LayoutGrid: LucideIcon = icon_LayoutGrid;
export const Link2: LucideIcon = icon_Link2;
export const List: LucideIcon = icon_List;
export const ListChecks: LucideIcon = icon_ListChecks;
export const ListFilter: LucideIcon = icon_ListFilter;
export const Loader2: LucideIcon = icon_Loader2;
export const Lock: LucideIcon = icon_Lock;
export const LogOut: LucideIcon = icon_LogOut;
export const Mail: LucideIcon = icon_Mail;
export const MailCheck: LucideIcon = icon_MailCheck;
export const MapPin: LucideIcon = icon_MapPin;
export const MapPinned: LucideIcon = icon_MapPinned;
export const Maximize2: LucideIcon = icon_Maximize2;
export const Megaphone: LucideIcon = icon_Megaphone;
export const Menu: LucideIcon = icon_Menu;
export const Minus: LucideIcon = icon_Minus;
export const Moon: LucideIcon = icon_Moon;
export const Paintbrush: LucideIcon = icon_Paintbrush;
export const Paperclip: LucideIcon = icon_Paperclip;
export const PauseCircle: LucideIcon = icon_PauseCircle;
export const Pencil: LucideIcon = icon_Pencil;
export const PenSquare: LucideIcon = icon_PenSquare;
export const Percent: LucideIcon = icon_Percent;
export const Phone: LucideIcon = icon_Phone;
export const PieChart: LucideIcon = icon_PieChart;
export const PiggyBank: LucideIcon = icon_PiggyBank;
export const Pin: LucideIcon = icon_Pin;
export const PlayCircle: LucideIcon = icon_PlayCircle;
export const Plus: LucideIcon = icon_Plus;
export const Power: LucideIcon = icon_Power;
export const Printer: LucideIcon = icon_Printer;
export const QrCode: LucideIcon = icon_QrCode;
export const Receipt: LucideIcon = icon_Receipt;
export const RefreshCw: LucideIcon = icon_RefreshCw;
export const Repeat: LucideIcon = icon_Repeat;
export const RotateCcw: LucideIcon = icon_RotateCcw;
export const RotateCw: LucideIcon = icon_RotateCw;
export const Save: LucideIcon = icon_Save;
export const Scale: LucideIcon = icon_Scale;
export const ScrollText: LucideIcon = icon_ScrollText;
export const Search: LucideIcon = icon_Search;
export const SearchX: LucideIcon = icon_SearchX;
export const Send: LucideIcon = icon_Send;
export const Settings: LucideIcon = icon_Settings;
export const Settings2: LucideIcon = icon_Settings2;
export const Share2: LucideIcon = icon_Share2;
export const Shield: LucideIcon = icon_Shield;
export const ShieldAlert: LucideIcon = icon_ShieldAlert;
export const ShieldCheck: LucideIcon = icon_ShieldCheck;
export const Siren: LucideIcon = icon_Siren;
export const Sliders: LucideIcon = icon_Sliders;
export const SlidersHorizontal: LucideIcon = icon_SlidersHorizontal;
export const Smartphone: LucideIcon = icon_Smartphone;
export const Sparkles: LucideIcon = icon_Sparkles;
export const Sprout: LucideIcon = icon_Sprout;
export const SquareParking: LucideIcon = icon_SquareParking;
export const Store: LucideIcon = icon_Store;
export const Sun: LucideIcon = icon_Sun;
export const Tag: LucideIcon = icon_Tag;
export const Target: LucideIcon = icon_Target;
export const Trash2: LucideIcon = icon_Trash2;
export const TreePine: LucideIcon = icon_TreePine;
export const Trees: LucideIcon = icon_Trees;
export const TrendingDown: LucideIcon = icon_TrendingDown;
export const TrendingUp: LucideIcon = icon_TrendingUp;
export const Trophy: LucideIcon = icon_Trophy;
export const Undo2: LucideIcon = icon_Undo2;
export const Upload: LucideIcon = icon_Upload;
export const UploadCloud: LucideIcon = icon_UploadCloud;
export const User: LucideIcon = icon_User;
export const UserCheck: LucideIcon = icon_UserCheck;
export const UserCircle: LucideIcon = icon_UserCircle;
export const UserCog: LucideIcon = icon_UserCog;
export const UserPlus: LucideIcon = icon_UserPlus;
export const Users: LucideIcon = icon_Users;
export const UserX: LucideIcon = icon_UserX;
export const Vote: LucideIcon = icon_Vote;
export const Wallet: LucideIcon = icon_Wallet;
export const Waves: LucideIcon = icon_Waves;
export const Wifi: LucideIcon = icon_Wifi;
export const Wrench: LucideIcon = icon_Wrench;
export const X: LucideIcon = icon_X;
export const XCircle: LucideIcon = icon_XCircle;
export const Zap: LucideIcon = icon_Zap;
export const ZoomIn: LucideIcon = icon_ZoomIn;
export const ZoomOut: LucideIcon = icon_ZoomOut;
